import React, { useEffect, useState } from "react";
import RegisterPage from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiGet, apiPost } from "../lib/api";

const RESULT = {
  Pass: { bg: "#DCFCE7", color: "#166534" },
  Fail: { bg: "#FEE2E2", color: "#7F1D1D" },
  NA: { bg: "#F3F4F6", color: "#374151" },
};
const RISK = {
  Low: { bg: "#DCFCE7", color: "#166534" },
  Medium: { bg: "#FEF3C7", color: "#854D0E" },
  High: { bg: "#FFE4E6", color: "#9F1239" },
  Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
};
const STATUS = {
  Open: { bg: "#FEF3C7", color: "#854D0E" },
  "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
  Closed: { bg: "#DCFCE7", color: "#166534" },
};

function normalizeStatus(status) {
  const value = String(status || "Open").toLowerCase();
  if (value === "closed") return "Closed";
  if (value === "in_progress" || value === "in progress") return "In Progress";
  return "Open";
}

function toBackendStatus(status) {
  const value = String(status || "Open").toLowerCase();
  if (value === "closed") return "closed";
  if (value === "in progress") return "in_progress";
  return "open";
}

function splitChecklistItems(text) {
  return String(text || "")
    .split(/\n|;|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapSubmission(row) {
  return {
    id: row.submission_code || `DAY-${row.id}`,
    site: row.site_name || "",
    date: row.submission_date || "",
    safetyOfficer: row.submitted_by || "",
    checklistItems: row.template_title || "Daily Safety Checklist",
    result: row.overall_status || "Pass",
    remarks: row.remarks || "",
    riskLevel: row.risk_level || "Medium",
    correctiveActionRequired: row.corrective_action_required || "No",
    responsible: row.responsible_person || "",
    targetDate: row.target_date || "",
    status: normalizeStatus(row.status),
    backendId: row.id,
    templateId: row.template_id,
    siteId: row.site_id,
  };
}

export default function DailySafetyChecklist() {
  const { data, setData, siteNames } = useHSE();
  const [siteIdByName, setSiteIdByName] = useState({});

  useEffect(() => {
    let cancelled = false;

    async function loadBackendData() {
      try {
        const sites = await apiGet("/api/sites");
        if (!cancelled && Array.isArray(sites)) {
          setSiteIdByName(
            Object.fromEntries(sites.map((site) => [site.name, site.id]))
          );
        }

        const submissions = await apiGet("/api/checklist-submissions");
        if (cancelled || !Array.isArray(submissions) || submissions.length === 0) return;

        setData((prev) => ({
          ...prev,
          dailyChecklists: submissions.map(mapSubmission),
        }));
      } catch (error) {
        console.warn("Daily checklist API unavailable, using local fallback data.", error);
      }
    }

    loadBackendData();

    return () => {
      cancelled = true;
    };
  }, [setData]);

  const saveChecklistSubmission = async (record) => {
    const checklistItems = splitChecklistItems(record.checklistItems);

    await apiPost("/api/checklist-submissions", {
      submission_code: record.id,
      template_id: null,
      site_id: siteIdByName[record.site] || null,
      equipment_tool_id: null,
      submitted_by: record.safetyOfficer,
      submission_date: record.date || new Date().toISOString().slice(0, 10),
      overall_status: record.result || "Pass",
      risk_level: record.riskLevel || "Medium",
      remarks: record.remarks || "",
      corrective_action_required: record.correctiveActionRequired || "No",
      responsible_person: record.responsible || "",
      target_date: record.targetDate || "",
      status: toBackendStatus(record.status),
      items: checklistItems.map((question) => ({
        question,
        result: record.result || "NA",
        remarks: record.remarks || "",
      })),
    });
  };

  return (
    <RegisterPage
      title="Daily Safety Checklist"
      subtitle="Safety Officer daily construction site checklist submissions"
      storeKey="dailyChecklists"
      idPrefix="DAY"
      onSaveRecord={saveChecklistSubmission}
      columns={[
        { key: "id", label: "Checklist ID" },
        { key: "site", label: "Site" },
        { key: "date", label: "Date" },
        { key: "safetyOfficer", label: "Safety Officer" },
        { key: "checklistItems", label: "Checklist Items" },
        { key: "result", label: "Pass / Fail / NA" },
        { key: "remarks", label: "Remarks / Findings" },
        { key: "riskLevel", label: "Risk Level" },
        { key: "correctiveActionRequired", label: "CA Required" },
        { key: "responsible", label: "Responsible" },
        { key: "targetDate", label: "Target Date" },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ result: RESULT, riskLevel: RISK, status: STATUS }}
      formFields={[
        { key: "id", label: "Checklist ID" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "date", label: "Date", type: "date" },
        { key: "safetyOfficer", label: "Safety Officer" },
        { key: "checklistItems", label: "Checklist Items", type: "textarea", full: true },
        { key: "result", label: "Pass / Fail / NA", type: "select", options: ["Pass", "Fail", "NA"] },
        { key: "remarks", label: "Remarks / Findings", type: "textarea", full: true },
        { key: "riskLevel", label: "Risk Level", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "correctiveActionRequired", label: "Corrective Action Required", type: "select", options: ["Yes", "No"] },
        { key: "responsible", label: "Responsible Person" },
        { key: "targetDate", label: "Target Date", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Closed"] },
      ]}
    />
  );
}

